import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Heart, LayoutTemplate, X, Loader2, Sparkles, Star,
  FileText, Image, Newspaper, Palette, Video, Layout, CreditCard,
  Menu as MenuIcon, Award, Users, MessageSquare, BarChart3, IdCard,
  Gift, Mail, Eye, Lock,
} from 'lucide-react';
import UpgradeModal from '../components/shared/UpgradeModal';
import AuthModal from '../components/Layout/AuthModal';
import { useTemplate } from '../lib/useTemplate';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import TemplateThumbnail from '../components/shared/TemplateThumbnail';
import usePageMeta from '../lib/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const USER_KEY = 'ivisora_user';

const getUserPlan = () => {
  try {
    const u = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    return u?.plan || 'free';
  } catch { return 'free'; }
};

const TYPE_META = {
  flyer:             { icon: FileText,      color: 'from-blue-500 to-cyan-500' },
  poster:            { icon: Image,         color: 'from-purple-500 to-pink-500' },
  brochure:          { icon: Newspaper,     color: 'from-green-500 to-emerald-500' },
  logo:              { icon: Palette,       color: 'from-orange-500 to-red-500' },
  youtube_thumbnail: { icon: Video,         color: 'from-red-500 to-rose-500' },
  youtube_banner:    { icon: Layout,        color: 'from-violet-500 to-purple-500' },
  business_card:     { icon: CreditCard,    color: 'from-teal-500 to-cyan-500' },
  menu:              { icon: MenuIcon,      color: 'from-amber-500 to-yellow-500' },
  certificate:       { icon: Award,         color: 'from-indigo-500 to-blue-500' },
  instagram_post:    { icon: Users,         color: 'from-pink-500 to-rose-500' },
  instagram_story:   { icon: Sparkles,      color: 'from-fuchsia-500 to-pink-500' },
  facebook_post:     { icon: Users,         color: 'from-blue-600 to-blue-400' },
  meme:              { icon: MessageSquare, color: 'from-lime-500 to-green-500' },
  infographic:       { icon: BarChart3,     color: 'from-sky-500 to-blue-500' },
  biodata:           { icon: IdCard,        color: 'from-emerald-500 to-teal-500' },
  pamphlet:          { icon: FileText,      color: 'from-rose-500 to-pink-500' },
  gift_certificate:  { icon: Gift,          color: 'from-red-500 to-orange-500' },
  invitation:        { icon: Mail,          color: 'from-purple-500 to-violet-500' },
  presentation:      { icon: Layout,        color: 'from-indigo-500 to-violet-500' },
  banner:            { icon: Image,         color: 'from-cyan-500 to-sky-500' },
  email_header:      { icon: Mail,          color: 'from-slate-500 to-gray-500' },
  other:             { icon: Sparkles,      color: 'from-violet-500 to-fuchsia-500' },
};
const FALLBACK_META = { icon: Sparkles, color: 'from-violet-500 to-fuchsia-500' };

/* ── Preview Modal ─────────────────────────────── */
const PreviewModal = ({ template, typeMap, onUse, onClose, userPlan, onUpgrade }) => {
  const typeName = typeMap[template.template_type_id]?.name || '';
  const pagesCount = template.template_json?.pages?.length ?? template.pages_count ?? 1;

  // Clamp the preview to max 80vh tall, width follows canvas aspect ratio.
  const canvasW = template.canvas_width || 1080;
  const canvasH = template.canvas_height || 1080;
  const maxH = 520; // image area max height (px)
  const maxW = 480;
  const ratio = canvasW / canvasH;
  let previewW = Math.min(maxW, maxH * ratio);
  let previewH = previewW / ratio;
  if (previewH > maxH) { previewH = maxH; previewW = previewH * ratio; }
  previewW = Math.round(previewW);
  previewH = Math.round(previewH);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal — width adapts to design aspect ratio.
          Width must equal previewW exactly so the preview area fills the
          modal frame edge-to-edge (no +N pixel compensation). */}
      <div
        className="relative z-10 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ width: previewW }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/25 hover:bg-black/50 flex items-center justify-center text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Thumbnail — live render via TemplateThumbnail (stored thumb overlays it when present) */}
        <div
          className="relative bg-gray-100 overflow-hidden"
          style={{ width: previewW, height: previewH }}
        >
          <TemplateThumbnail template={template}>
            {template.is_premium && (
              <div className="absolute top-3 left-3 z-10">
                <span className="flex items-center gap-1 text-xs font-bold px-2.5 py-1 bg-amber-400 text-amber-900 rounded-full shadow">
                  <Star className="w-3 h-3 fill-amber-900" /> PRO
                </span>
              </div>
            )}
            {pagesCount > 1 && (
              <div className="absolute bottom-3 left-3 z-10">
                <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 bg-black/60 text-white rounded-full backdrop-blur-sm">
                  <FileText className="w-3 h-3" /> {pagesCount} Pages
                </span>
              </div>
            )}
          </TemplateThumbnail>
        </div>

        {/* Info + actions footer */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-gray-900 truncate">{template.name}</h3>
              {typeName && <p className="text-xs text-gray-400 mt-0.5 truncate">{typeName}</p>}
            </div>
            <span className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
              <Heart className="w-3.5 h-3.5" /> {template.likes || 0}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 h-9 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            {template.is_premium && userPlan === 'free' ? (
              <button
                onClick={() => { onClose(); onUpgrade(); }}
                className="flex-1 h-9 rounded-xl bg-amber-400 hover:bg-amber-500 text-amber-900 text-sm font-semibold transition-colors flex items-center justify-center gap-1.5"
              >
                <Lock className="w-3.5 h-3.5" /> Unlock
              </button>
            ) : (
              <button
                onClick={() => { onClose(); onUse(template); }}
                className="flex-1 h-9 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors shadow-sm shadow-violet-200"
              >
                Use Template
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Template Card ─────────────────────────────── */
const TemplateCard = ({ template, typeMap, onUse, onPreview, userPlan, onUpgrade }) => {
  const typeName = typeMap[template.template_type_id]?.name || '';
  const pagesCount = template.template_json?.pages?.length ?? template.pages_count ?? 1;
  const isLocked = template.is_premium && userPlan === 'free';

  const handleUseClick = () => isLocked ? onUpgrade() : onUse(template);

  return (
    <div className="group flex flex-col rounded-xl overflow-hidden bg-white border border-gray-100 hover:border-violet-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      {/* Thumbnail — 1:1 square, cover-scales the real canvas design */}
      <TemplateThumbnail template={template} className="rounded-none group-hover:scale-[1.03] transition-transform duration-500">
        {/* PRO badge */}
        {template.is_premium && (
          <div className="absolute top-2 right-2 z-10">
            <span className="flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 bg-amber-400 text-amber-900 rounded-full shadow">
              <Star className="w-2.5 h-2.5 fill-amber-900" /> PRO
            </span>
          </div>
        )}

        {/* Lock overlay for free users on premium templates */}
        {isLocked && (
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px] z-[5] flex items-center justify-center">
            <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
              <Lock className="w-5 h-5 text-amber-500" />
            </div>
          </div>
        )}

        {/* Multi-page badge */}
        {pagesCount > 1 && (
          <div className="absolute bottom-2 left-2 z-10">
            <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-black/60 text-white rounded-full backdrop-blur-sm">
              <FileText className="w-2.5 h-2.5" /> {pagesCount} Pages
            </span>
          </div>
        )}

        {/* Hover action overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center gap-2 px-4 z-10">
          <button
            onClick={handleUseClick}
            className={`w-full h-9 rounded-lg text-sm font-semibold transition-colors shadow-lg flex items-center justify-center gap-1.5 ${isLocked ? 'bg-amber-400 hover:bg-amber-500 text-amber-900' : 'bg-violet-600 hover:bg-violet-700 text-white'}`}
          >
            {isLocked ? <><Lock className="w-3.5 h-3.5" /> Unlock</> : 'Use Template'}
          </button>
          <button
            onClick={() => onPreview(template)}
            className="w-full h-9 bg-white hover:bg-gray-50 text-gray-900 rounded-lg text-sm font-semibold transition-colors shadow-lg flex items-center justify-center gap-1.5"
          >
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
        </div>
      </TemplateThumbnail>

      {/* Card footer — fixed height so all footers align */}
      <div className="px-3 py-2.5 flex flex-col justify-center" style={{ height: 56 }}>
        <p className="text-sm font-semibold text-gray-900 truncate leading-tight">{template.name}</p>
        <div className="flex items-center justify-between mt-1">
          {typeName ? (
            <span className="text-xs text-gray-400 truncate pr-2">{typeName}</span>
          ) : (
            <span />
          )}
          <span className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
            <Heart className="w-3 h-3" /> {template.likes || 0}
          </span>
        </div>
      </div>
    </div>
  );
};

// The public list is served a page at a time (the server caps a page at 100).
const PAGE_SIZE = 48;

/* ── Page ──────────────────────────────────────── */
const TemplatesPage = () => {
  const navigate = useNavigate();
  usePageMeta('Templates', 'Browse design templates for social posts, flyers, invitations and more, and customise them in the iVisora editor.', { path: '/templates' });
  const location = useLocation();

  const [templateTypes, setTemplateTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(null);          // how many match on the server; null until it says
  const [nextPage, setNextPage] = useState(2);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const requestRef = useRef(0);                      // bumped when the filter changes, so a late page is dropped
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState(location.state?.templateTypeId || null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [authPrompt, setAuthPrompt] = useState(null);   // { onSuccess } while asking a guest to sign in
  const userPlan = getUserPlan();

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Template types — after loading, resolve typeSlug from navigation state to an ID
  useEffect(() => {
    fetch(`${API_URL}/api/public/template-types`)
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        const types = Array.isArray(d) ? d : [];
        setTemplateTypes(types);
        // location.state can carry typeSlug (from HomePage category click) or templateTypeId (direct ID)
        const slug = location.state?.typeSlug;
        if (slug) {
          const match = types.find(t => t.slug === slug);
          if (match) setSelectedTypeId(match.id);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // One page of the public list. X-Total-Count is the size of the whole result, not of this page.
  const fetchPage = (page) => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), page: String(page) });
    if (selectedTypeId) params.set('template_type_id', selectedTypeId);
    if (debouncedSearch) params.set('search', debouncedSearch);
    return fetch(`${API_URL}/api/public/templates?${params}`).then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const rows = await r.json();
      const reported = Number.parseInt(r.headers?.get?.('X-Total-Count'), 10);
      return { rows: Array.isArray(rows) ? rows : [], total: Number.isFinite(reported) ? reported : null };
    });
  };

  // Templates (filtered by type and/or search) — the first page; a new filter starts again from it
  useEffect(() => {
    const request = ++requestRef.current;
    setLoading(true);
    setLoadMoreFailed(false);
    fetchPage(1)
      .then(({ rows, total: reported }) => {
        if (request !== requestRef.current) return;
        setTemplates(rows);
        setTotal(reported);
        setNextPage(2);
        setHasMore(rows.length > 0 && (reported !== null ? rows.length < reported : rows.length === PAGE_SIZE));
      })
      .catch(() => {
        if (request !== requestRef.current) return;
        setTemplates([]);
        setTotal(null);
        setHasMore(false);
      })
      .finally(() => { if (request === requestRef.current) setLoading(false); });
  }, [selectedTypeId, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    const request = requestRef.current;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    fetchPage(nextPage)
      .then(({ rows, total: reported }) => {
        if (request !== requestRef.current) return;      // the filter changed while this page was on its way
        const seen = new Set(templates.map(t => t.id));
        const fresh = rows.filter(t => !seen.has(t.id));
        const known = reported ?? total;
        setTemplates([...templates, ...fresh]);
        if (reported !== null) setTotal(reported);
        setNextPage(nextPage + 1);
        // That is all when the server says so — or when a page brings nothing new
        // (a server that does not page yet answers every request with page one).
        setHasMore(fresh.length > 0 && (known !== null ? nextPage * PAGE_SIZE < known : rows.length === PAGE_SIZE));
      })
      .catch(() => { if (request === requestRef.current) setLoadMoreFailed(true); })
      .finally(() => { if (request === requestRef.current) setLoadingMore(false); });
  };

  // What the page says it has: the server's count, not the number of cards loaded so far.
  const shownTotal = total ?? templates.length;

  const handleUseTemplate = (template) => {
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    } catch { user = null; }
    useTemplate(template, {
      navigate,
      user,
      // Sign in right here; the chosen template resumes once the visitor is in.
      openAuthModal: ({ onSuccess }) => setAuthPrompt({ onSuccess }),
      openUpgradeModal: () => setShowUpgradeModal(true),
      toast: ({ title }) => {
        if (typeof window !== 'undefined') window.alert(title);
      },
    });
  };

  const typeMap = Object.fromEntries(templateTypes.map(t => [t.id, t]));
  const selectedType = templateTypes.find(t => t.id === selectedTypeId);
  const isFiltered = selectedTypeId || debouncedSearch;

  return (
    <div className="min-h-screen bg-[#f8f8fc]">
      <Header />

      {/* ── HERO ─────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 text-white">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -right-24 w-[30rem] h-[30rem] bg-fuchsia-400/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3 tracking-tight">
            Explore Templates
          </h1>
          <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
            Find the perfect starting point for your next design project.
          </p>
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search templates (e.g., Instagram, Wedding, Business Card)"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-14 pr-12 h-14 text-base bg-white text-gray-900 border-0 rounded-2xl shadow-2xl focus:outline-none focus:ring-2 focus:ring-white/50 placeholder-gray-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── BODY: SIDEBAR + GRID ─────────────────── */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-6 items-start">

          {/* ── LEFT SIDEBAR ─────────────────────── */}
          <aside className="hidden md:flex flex-col w-56 flex-shrink-0 sticky top-6 self-start bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">
              Categories
            </p>
            <nav className="flex flex-col">
              {/* All Templates */}
              <button
                onClick={() => setSelectedTypeId(null)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all text-left border-l-2 ${
                  !selectedTypeId
                    ? 'border-violet-600 bg-violet-50 text-violet-700 font-semibold'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-medium'
                }`}
              >
                <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                  !selectedTypeId
                    ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500'
                    : 'bg-gray-100'
                }`}>
                  <LayoutTemplate className={`w-3.5 h-3.5 ${!selectedTypeId ? 'text-white' : 'text-gray-500'}`} />
                </div>
                <span className="whitespace-nowrap flex-1">All Templates</span>
                {!selectedTypeId && (
                  <span className="text-[10px] font-bold text-violet-400">{shownTotal}</span>
                )}
              </button>

              {/* Template Types only */}
              {templateTypes.map(tt => {
                const meta = TYPE_META[tt.slug] || FALLBACK_META;
                const IconComp = meta.icon;
                const active = selectedTypeId === tt.id;
                return (
                  <button
                    key={tt.id}
                    onClick={() => setSelectedTypeId(active ? null : tt.id)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all text-left border-l-2 w-full ${
                      active
                        ? 'border-violet-600 bg-violet-50 text-violet-700 font-semibold'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-medium'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${
                      active ? 'from-violet-500 to-fuchsia-500' : meta.color
                    }`}>
                      <IconComp className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="whitespace-nowrap flex-1">{tt.name}</span>
                    {active && templates.length > 0 && (
                      <span className="text-[10px] font-bold text-violet-400">{shownTotal}</span>
                    )}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* ── RIGHT: GRID ──────────────────────── */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {selectedType
                    ? `${selectedType.name} Templates`
                    : debouncedSearch
                    ? `Results for "${debouncedSearch}"`
                    : 'All Templates'}
                </h2>
                {!loading && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {shownTotal} template{shownTotal !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              {isFiltered && (
                <button
                  onClick={() => { setSelectedTypeId(null); setSearchQuery(''); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-full hover:border-gray-300 transition-colors"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>

            {/* Mobile: horizontal pill scroll */}
            <div className="flex gap-2 overflow-x-auto pb-3 mb-5 md:hidden scrollbar-hide">
              <button
                onClick={() => setSelectedTypeId(null)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  !selectedTypeId ? 'bg-violet-600 text-white' : 'bg-white border border-gray-200 text-gray-700'
                }`}
              >
                All
              </button>
              {templateTypes.map(tt => (
                <button
                  key={tt.id}
                  onClick={() => setSelectedTypeId(selectedTypeId === tt.id ? null : tt.id)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    selectedTypeId === tt.id ? 'bg-violet-600 text-white' : 'bg-white border border-gray-200 text-gray-700'
                  }`}
                >
                  {tt.icon} {tt.name}
                </button>
              ))}
            </div>

            {/* Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
              </div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                  <LayoutTemplate className="w-9 h-9 text-gray-300" />
                </div>
                <p className="text-lg font-semibold text-gray-500">No templates found</p>
                <p className="text-gray-400 text-sm mt-1">Try a different category or search term.</p>
                {isFiltered && (
                  <button
                    onClick={() => { setSelectedTypeId(null); setSearchQuery(''); }}
                    className="mt-4 px-5 py-2 bg-violet-600 text-white rounded-full text-sm font-semibold hover:bg-violet-700 transition-colors"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
                  {templates.map(t => (
                    <div key={t.id} className="break-inside-avoid mb-4">
                      <TemplateCard
                        template={t}
                        typeMap={typeMap}
                        onUse={handleUseTemplate}
                        onPreview={setPreviewTemplate}
                        userPlan={userPlan}
                        onUpgrade={() => setShowUpgradeModal(true)}
                      />
                    </div>
                  ))}
                </div>
                {(hasMore || loadMoreFailed) && (
                  <div className="flex flex-col items-center gap-2 mt-8">
                    {total !== null && (
                      <p className="text-xs text-gray-400" data-testid="templates-progress">
                        Showing {templates.length} of {total}
                      </p>
                    )}
                    {loadMoreFailed && (
                      <p className="text-xs text-red-500" role="alert">
                        Couldn’t load more templates. Check your connection and try again.
                      </p>
                    )}
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      data-testid="templates-load-more"
                      className="inline-flex items-center gap-2 px-6 py-2.5 bg-violet-600 text-white rounded-full text-sm font-semibold hover:bg-violet-700 transition-colors disabled:opacity-60"
                    >
                      {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                      {loadingMore ? 'Loading…' : loadMoreFailed ? 'Try again' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />

      {/* ── PREVIEW MODAL ────────────────────────── */}
      {previewTemplate && (
        <PreviewModal
          template={previewTemplate}
          typeMap={typeMap}
          onUse={handleUseTemplate}
          onClose={() => setPreviewTemplate(null)}
          userPlan={userPlan}
          onUpgrade={() => { setPreviewTemplate(null); setShowUpgradeModal(true); }}
        />
      )}

      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}

      {authPrompt && (
        <AuthModal
          open
          defaultTab="signup"
          onClose={() => setAuthPrompt(null)}
          onAuthSuccess={(authedUser) => {
            const resume = authPrompt.onSuccess;
            setAuthPrompt(null);
            resume(authedUser);
          }}
        />
      )}
    </div>
  );
};

export default TemplatesPage;
